import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { X, Send, Loader2 } from "lucide-react";
import { clientApi } from "../lib/api";
import { toast } from "sonner";

export default function DemandModal({ onClose }) {
  const [cfg, setCfg] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState({
    campaign_id: "", product_id: "",
    department: "", work_front: "", piece_type: "",
    objectives: "", formats: "", budget: "", prohibitions: "",
    obligations: "", references: "", structure: "", description: "",
    desired_deadline: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([
      clientApi.get("/briefing-config"),
      clientApi.get("/campaigns"),
      clientApi.get("/products"),
    ]).then(([c, ca, pr]) => {
      setCfg(c.data); setCampaigns(ca.data); setProducts(pr.data);
    });
  }, []);

  const upd = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const selectedPiece = cfg?.piece_types?.find((p) => p.key === form.piece_type || p.label === form.piece_type);
  const carencia = selectedPiece?.carencia_days ?? cfg?.carencia_days_default ?? 2;
  const prazoEstimado = selectedPiece?.prazo_days;

  const submit = async () => {
    if (!form.department || !form.work_front || !form.piece_type) {
      toast.error("Preencha Departamento, Frente e Tipo de Peça.");
      return;
    }
    setBusy(true);
    try {
      await clientApi.post("/demands", form);
      toast.success("Demanda enviada!");
      onClose();
    } catch (e) {
      toast.error("Erro ao enviar demanda.");
    } finally { setBusy(false); }
  };

  return (
    <motion.div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      data-testid="demand-modal"
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <motion.div
        initial={{ y: 40, opacity: 0, scale: 0.96 }} animate={{ y: 0, opacity: 1, scale: 1 }} exit={{ y: 40, opacity: 0 }}
        className="relative bg-[#141414] border border-white/10 rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto no-scrollbar"
      >
        <div className="sticky top-0 z-10 bg-[#141414] border-b border-white/10 p-5 flex justify-between items-center">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-accent-orange">Novo Briefing</div>
            <div className="font-display text-2xl">Demandar</div>
          </div>
          <button onClick={onClose} data-testid="demand-close" className="p-2 hover:bg-white/5 rounded-full"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-4">
          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Produto">
              <select value={form.product_id} onChange={(e) => upd("product_id", e.target.value)} className="w-full bg-black border border-white/10 rounded-lg px-3 py-2.5 text-sm" data-testid="demand-product">
                <option value="">— sem produto —</option>
                {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </Field>
            <Field label="Campanha">
              <select value={form.campaign_id} onChange={(e) => upd("campaign_id", e.target.value)} className="w-full bg-black border border-white/10 rounded-lg px-3 py-2.5 text-sm" data-testid="demand-campaign">
                <option value="">— sem campanha —</option>
                {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </Field>
          </div>

          <div className="grid md:grid-cols-3 gap-3">
            <Field label="Departamento *" required>
              <select value={form.department} onChange={(e) => upd("department", e.target.value)} className="w-full bg-black border border-white/10 rounded-lg px-3 py-2.5 text-sm" data-testid="demand-department">
                <option value="">Selecione…</option>
                {(cfg?.departments || []).map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </Field>
            <Field label="Frente de Trabalho *" required>
              <select value={form.work_front} onChange={(e) => upd("work_front", e.target.value)} className="w-full bg-black border border-white/10 rounded-lg px-3 py-2.5 text-sm" data-testid="demand-workfront">
                <option value="">Selecione…</option>
                {(cfg?.work_fronts || []).map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </Field>
            <Field label="Tipo de Peça *" required>
              <select value={form.piece_type} onChange={(e) => upd("piece_type", e.target.value)} className="w-full bg-black border border-white/10 rounded-lg px-3 py-2.5 text-sm" data-testid="demand-piece-type">
                <option value="">Selecione…</option>
                {(cfg?.piece_types || []).map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
              </select>
            </Field>
          </div>

          {selectedPiece && (
            <div className="text-xs text-neutral-400 bg-accent-orange/10 border border-accent-orange/30 rounded-lg px-3 py-2">
              ⏱ Carência: <b className="text-white">{carencia} dias</b>
              {prazoEstimado != null && <> · Prazo estimado: <b className="text-white">{prazoEstimado} dias</b></>}
              <span className="ml-1">(prazo começa a contar após a carência)</span>
            </div>
          )}

          <Field label="Objetivo(s)"><textarea value={form.objectives} onChange={(e) => upd("objectives", e.target.value)} rows={2} className="w-full bg-black border border-white/10 rounded-lg px-3 py-2.5 text-sm" /></Field>
          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Formato(s)"><input value={form.formats} onChange={(e) => upd("formats", e.target.value)} className="w-full bg-black border border-white/10 rounded-lg px-3 py-2.5 text-sm" /></Field>
            <Field label="Orçamento (Budget)"><input value={form.budget} onChange={(e) => upd("budget", e.target.value)} placeholder="R$ …" className="w-full bg-black border border-white/10 rounded-lg px-3 py-2.5 text-sm" /></Field>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Proibições"><textarea value={form.prohibitions} onChange={(e) => upd("prohibitions", e.target.value)} rows={2} className="w-full bg-black border border-white/10 rounded-lg px-3 py-2.5 text-sm" /></Field>
            <Field label="Obrigatoriedades"><textarea value={form.obligations} onChange={(e) => upd("obligations", e.target.value)} rows={2} className="w-full bg-black border border-white/10 rounded-lg px-3 py-2.5 text-sm" /></Field>
          </div>
          <Field label="Referências (links, briefing prévio, etc.)"><textarea value={form.references} onChange={(e) => upd("references", e.target.value)} rows={2} className="w-full bg-black border border-white/10 rounded-lg px-3 py-2.5 text-sm" /></Field>
          <Field label="Estrutura"><textarea value={form.structure} onChange={(e) => upd("structure", e.target.value)} rows={2} className="w-full bg-black border border-white/10 rounded-lg px-3 py-2.5 text-sm" /></Field>
          <Field label="Descrição detalhada"><textarea value={form.description} onChange={(e) => upd("description", e.target.value)} rows={3} className="w-full bg-black border border-white/10 rounded-lg px-3 py-2.5 text-sm" /></Field>
          <Field label="Prazo Pretendido (opcional)"><input type="date" value={form.desired_deadline} onChange={(e) => upd("desired_deadline", e.target.value)} className="w-full bg-black border border-white/10 rounded-lg px-3 py-2.5 text-sm" /></Field>

          <button onClick={submit} disabled={busy} data-testid="demand-submit" className="w-full flex items-center justify-center gap-2 bg-accent-orange hover:bg-accent-yellow hover:text-black text-white font-bold py-3.5 rounded-full transition-all disabled:opacity-50 mt-4">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} {busy ? "Enviando…" : "Enviar Demanda"}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1.5">{label}</div>
      {children}
    </label>
  );
}
